import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Row,
  Col,
  Spinner,
  Button,
  Table,
  Popover,
  OverlayTrigger
} from 'react-bootstrap';
import { ChevronDown, ChevronRight, Check, AlertCircle } from 'react-feather';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';

import { closeImportWindow, importStudents } from '../../actions/student';
import {
  SubTitleH2,
  DefaultButton,
  StudentsImportModal
} from '../../components';
import Pagination from '../../components/DataTable/Pagination';

const StyledTable = styled(Table)`
  table-layout: fixed !important;
  font-size: 14px !important;
  min-width: 1000px !important;
`;

const StyledHeaderCell = styled.th`
  padding: 8px !important;
  font-size: 13px !important;
`;

const StyledCell = styled.td`
  padding: 8px !important;
  height: 45px !important;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const ExpandCell = styled(StyledCell)`
  padding: 4px !important;
  width: 40px !important;
  text-align: center;
  vertical-align: middle;
`;

const ExpandHeaderCell = styled(StyledHeaderCell)`
  padding: 4px !important;
  width: 40px !important;
  text-align: center;
  vertical-align: middle;
`;

const ExpandButton = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
`;

const ExpandedSectionContainer = styled.div`
  padding: 8px;
  margin-left: 16px;
  margin-right: 16px;
`;

const ExpandedSectionTitle = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 14px;
`;

const ExpandedSectionTable = styled(Table)`
  font-size: 13px;

  th,
  td {
    padding: 8px !important;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto !important;
  width: 100%;
  min-height: 550px !important;
`;

const TruncatedText = ({ children }) => {
  const text = typeof children === 'string' ? children : String(children);

  return (
    <div
      title={text}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'help'
      }}
    >
      {children}
    </div>
  );
};

const ExpandedSection = ({ data }) => {
  const additionalSections = data.section_details.slice(1);

  if (additionalSections.length === 0) return null;

  return (
    <ExpandedSectionContainer>
      <ExpandedSectionTitle>Secciones adicionales:</ExpandedSectionTitle>
      <ExpandedSectionTable striped bordered size="sm">
        <thead>
          <tr>
            <StyledHeaderCell>Año</StyledHeaderCell>
            <StyledHeaderCell>Establecimiento</StyledHeaderCell>
            <StyledHeaderCell>Nivel</StyledHeaderCell>
            <StyledHeaderCell>Sección</StyledHeaderCell>
          </tr>
        </thead>
        <tbody>
          {additionalSections.map(section => (
            <tr
              key={`${section.import_year}-${section.import_establishment?.id}-${section.import_grade}-${section.import_section}`}
            >
              <StyledCell>{section.import_year}</StyledCell>
              <StyledCell>
                <TruncatedText>
                  {section.import_establishment?.name}
                </TruncatedText>
              </StyledCell>
              <StyledCell>{section.import_grade}</StyledCell>
              <StyledCell>{section.import_section}</StyledCell>
            </tr>
          ))}
        </tbody>
      </ExpandedSectionTable>
    </ExpandedSectionContainer>
  );
};

const ImportStudentsPreview = () => {
  const [showModal, setShowModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    ongoingRequest: { postSoftImport, postImport },
    softImportedStudents
  } = useSelector(state => state.student);
  const dispatch = useDispatch();

  const toggleRow = index => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleChangePage = useCallback(page => {
    setCurrentPage(page);
    setExpandedRows(new Set());
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage, page) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(page);
    setExpandedRows(new Set());
  }, []);

  const paginatedStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return softImportedStudents.slice(startIndex, endIndex);
  }, [softImportedStudents, currentPage, rowsPerPage]);

  useEffect(() => {
    if (!showModal) return;

    if (showModal && !postSoftImport) {
      setShowModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postSoftImport]);

  const handleUpload = () => {
    dispatch(closeImportWindow());
    setCurrentPage(1);

    setShowModal(true);
  };

  const handleImportStudents = () => {
    dispatch(importStudents({ body: { students: softImportedStudents } }));
  };

  const studentsWithErrors = softImportedStudents.filter(
    student => student.full_errors.length > 0
  );

  const canImportStudents =
    studentsWithErrors.length > 0 || softImportedStudents.length === 0;

  return (
    <>
      <div className="section">
        <Row>
          <Col
            md={12}
            className="d-flex justify-content-between align-items-center"
          >
            <SubTitleH2 text="Estudiantes" />
            <div>
              <DefaultButton onClick={handleUpload} text="Importar" />
            </div>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex flex-column gap-1-5">
            {softImportedStudents.length > 0 && (
              <div className="text-left mr-5" color="black">
                Importación realizada. Por favor validar la información y dar
                click en &quot;Confirmar&quot;
              </div>
            )}

            {studentsWithErrors.length > 0 && (
              <div style={{ color: 'red' }}>
                {studentsWithErrors.length} Estudiantes con errores.
              </div>
            )}
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={12}>
            {postSoftImport ? (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <div className="mt-4">
                <TableContainer>
                  <StyledTable striped bordered hover>
                    <thead>
                      <tr>
                        <ExpandHeaderCell />
                        <StyledHeaderCell style={{ width: '5%' }}>
                          Estado
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '9%' }}>
                          RUT
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '11%' }}>
                          Nombres
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '11%' }}>
                          Apellidos
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '6%' }}>
                          Genero
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '14%' }}>
                          Descripcion
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '5%' }}>
                          Año
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '15%' }}>
                          Establecimiento
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '9%' }}>
                          Nivel
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '6%' }}>
                          Seccion
                        </StyledHeaderCell>
                        <StyledHeaderCell style={{ width: '6%' }}>
                          Congelado
                        </StyledHeaderCell>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStudents.map((student, index) => {
                        const actualIndex =
                          (currentPage - 1) * rowsPerPage + index;
                        const hasAdditionalSections =
                          student.section_details?.length > 1;
                        const isExpanded = expandedRows.has(actualIndex);

                        return (
                          <React.Fragment key={student.id || actualIndex}>
                            <tr>
                              <ExpandCell>
                                {hasAdditionalSections ? (
                                  <ExpandButton
                                    onClick={() => toggleRow(actualIndex)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleRow(actualIndex);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown />
                                    ) : (
                                      <ChevronRight />
                                    )}
                                  </ExpandButton>
                                ) : null}
                              </ExpandCell>
                              <StyledCell>
                                {student.full_errors.length > 0 ? (
                                  <OverlayTrigger
                                    placement="right"
                                    overlay={
                                      <Popover id={`popover-${actualIndex}`}>
                                        <Popover.Content>
                                          <ul className="mb-0">
                                            {student.full_errors.map(e => (
                                              <li key={uuidv4()}>{e}</li>
                                            ))}
                                          </ul>
                                        </Popover.Content>
                                      </Popover>
                                    }
                                  >
                                    <div className="text-danger cursor-pointer">
                                      <AlertCircle />
                                    </div>
                                  </OverlayTrigger>
                                ) : (
                                  <div className="text-success">
                                    <Check />
                                  </div>
                                )}
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>{student.rut}</TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>{student.names}</TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.last_names}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.gender?.name}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.description?.name?.length > 0
                                    ? student.description.name
                                    : ''}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>{student.import_year}</StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.import_establishment?.name}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.import_grade}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>
                                <TruncatedText>
                                  {student.import_section}
                                </TruncatedText>
                              </StyledCell>
                              <StyledCell>{student.frozen}</StyledCell>
                            </tr>
                            {isExpanded && hasAdditionalSections && (
                              <tr>
                                <StyledCell colSpan={12}>
                                  <ExpandedSection data={student} />
                                </StyledCell>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </StyledTable>
                </TableContainer>
                {softImportedStudents.length > 0 && (
                  <div className="d-flex justify-content-end mt-3">
                    <Pagination
                      rowsPerPage={rowsPerPage}
                      rowCount={softImportedStudents.length}
                      currentPage={currentPage}
                      onChangePage={handleChangePage}
                      onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col className="d-flex justify-content-end">
            <Button
              className={`button-with-loader ${postImport &&
                'loading'} justify-content-center align-items-center`}
              disabled={canImportStudents || postImport}
              onClick={handleImportStudents}
            >
              <span className="text">Confirmar</span>
              <Spinner
                className="spinner ml-auto mr-auto"
                animation="border"
                variant="danger"
                size="sm"
              />
            </Button>
          </Col>
        </Row>
      </div>
      <StudentsImportModal
        show={showModal}
        onHide={() => setShowModal(false)}
      />
    </>
  );
};

export default ImportStudentsPreview;
